import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonComponent,
  CheckboxComponent,
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import { TranslationService, TranslatePipe } from '../../../../core/i18n';
import type { AddressAutocompleteSelection } from '../../../../core/models/address-autocomplete';
import { buildCanadianProvinceOptions } from '../../constants/canadian-provinces';
import type { AddressSectionKey } from '../../models/address-form.model';
import {
  blurAddressField,
  collectAddressSectionErrors,
  getAddressFieldErrorMessage,
  type AddressValidatedFieldKey,
} from '../../utils/address-form-errors';
import { detectPostalKeywordInAddress } from '../../utils/detect-postal-keyword-address';
import { FloatingSelect } from '../../../../shared/components/floating-select/floating-select';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import {
  FormErrorSummary,
  type FormErrorSummaryItem,
} from '../../../../shared/components/form-error-summary/form-error-summary';
import { PhoneFieldGroup } from '../phone-field-group/phone-field-group';
import { StreetAddressAutocompleteField } from '../../../../shared/components/street-address-autocomplete-field/street-address-autocomplete-field';
import { DisableBrowserAutofillDirective } from '../../../../shared/directives/disable-browser-autofill.directive';
import {
  AddressSuggestedAddressPanel,
  type AddressSuggestionRole,
} from '../address-suggested-address-panel/address-suggested-address-panel';
import { OptionalAddressLines } from '../optional-address-lines/optional-address-lines';
import type { AddressSuggestionPresentation } from '../../utils/map-validate-results-to-suggestions';

/**
 * Reusable Ship From or Ship To address section with expandable lines and optional return toggle.
 * Rural route / PO Box helper and error banner update on every keystroke in the street address field.
 */
@Component({
  selector: 'app-address-form-section',
  imports: [
    ReactiveFormsModule,
    TypographyComponent,
    InputComponent,
    CheckboxComponent,
    ButtonComponent,
    TooltipComponent,
    IconComponent,
    TranslatePipe,
    PhoneFieldGroup,
    FormErrorSummary,
    FloatingSelect,
    AlertToast,
    StreetAddressAutocompleteField,
    AddressSuggestedAddressPanel,
    OptionalAddressLines,
    DisableBrowserAutofillDirective,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  templateUrl: './address-form-section.html',
  styleUrl: './address-form-section.scss',
})
export class AddressFormSection implements OnInit {
  private readonly translationService = inject(TranslationService);
  private readonly controlContainer = inject(ControlContainer);
  private readonly destroyRef = inject(DestroyRef);

  /** Incremented on every street address or address line 3 change to drive rural route detection. */
  private readonly ruralRouteVersion = signal(0);

  /** Incremented on field blur so inline errors refresh after leave without submit. */
  private readonly fieldInteractionVersion = signal(0);

  /** Expands Address Line 2 when a rural route or PO Box keyword is detected. */
  private readonly autoExpandRuralAddressLines = effect(() => {
    if (this.showRuralRouteInfo() && !this.addressLinesExpanded()) {
      this.expandLinesRequested.emit();
    }
  });

  readonly sectionKey = input.required<AddressSectionKey>();
  readonly addressLinesExpanded = input(false);
  readonly showReturnAddressToggle = input(false);
  readonly useDifferentReturnAddress = input(false);
  readonly submitted = input(false);
  readonly validationAttempt = input(0);
  readonly addressSuggestion = input<AddressSuggestionPresentation | null>(null);
  /** Non-blocking missing unit/suite warning after AVS (Ship To only). */
  readonly showMissingUnitSuiteWarning = input(false);

  /** Nested address form group bound via formGroupName from the parent create-shipment form. */
  protected get addressFormGroup(): FormGroup {
    const rootForm = this.controlContainer.control as FormGroup;
    return rootForm.get(this.sectionKey()) as FormGroup;
  }

  readonly clearRequested = output<void>();
  readonly expandLinesRequested = output<void>();
  readonly useDifferentReturnAddressChange = output<boolean>();
  /** Emits the chosen autocomplete item for OM-94 field population. */
  readonly streetAddressSuggestionSelected = output<AddressAutocompleteSelection>();
  readonly useSuggestedAddress = output<void>();
  readonly keepSuggestedAddressEntry = output<void>();

  protected readonly suggestionRole = computed((): AddressSuggestionRole =>
    this.sectionKey() === 'shipFrom' ? 'sender' : 'recipient',
  );

  protected readonly suggestionPanelId = computed(
    () => `ship-from-ship-to-suggested-${this.sectionKey()}`,
  );

  protected readonly streetDescribedByIds = computed((): readonly string[] =>
    this.addressSuggestion() ? [`${this.suggestionPanelId()}-description`] : [],
  );

  protected readonly provinceOptions = computed(() => {
    this.translationService.revision();
    return buildCanadianProvinceOptions((key) => this.translationService.translate(key));
  });

  protected readonly sectionTitleKey = computed(
    () => `createShipment.sections.${this.sectionKey()}.title`,
  );

  protected readonly notificationsKey = computed(
    () => `createShipment.sections.${this.sectionKey()}.receiveNotifications`,
  );

  protected readonly attentionLabelKey = computed(() =>
    this.sectionKey() === 'shipFrom'
      ? 'createShipment.fields.contactName'
      : 'createShipment.fields.attentionTo',
  );

  /**
   * i18n key for the Contact Name / Attention To info tooltip.
   * Ship From uses sender copy; Ship To and return address keep recipient copy.
   */
  protected readonly attentionTooltipKey = computed(() =>
    this.sectionKey() === 'shipFrom'
      ? 'createShipment.fields.contactNameTooltip'
      : 'createShipment.fields.attentionToTooltip',
  );

  /**
   * Short accessible name for the Contact Name / Attention To info button.
   * Full help text is exposed via aria-describedby, not aria-label.
   */
  protected readonly attentionTooltipAriaKey = computed(() =>
    this.sectionKey() === 'shipFrom'
      ? 'createShipment.fields.contactNameTooltipAria'
      : 'createShipment.fields.attentionToTooltipAria',
  );

  /** Stable id for the Contact Name / Attention To tooltip description. */
  protected readonly contactTooltipDescId = computed(
    () => `address-${this.sectionKey()}-contact-tooltip-desc`,
  );

  /** Stable id for the Ship From postal-code tooltip description. */
  protected readonly postalTooltipDescId = computed(
    () => `address-${this.sectionKey()}-postal-tooltip-desc`,
  );

  /** Stable id for the Ship From email tooltip description. */
  protected readonly emailTooltipDescId = computed(
    () => `address-${this.sectionKey()}-email-tooltip-desc`,
  );

  /** Stable id tying the section landmark to its visible heading. */
  protected readonly sectionTitleId = computed(() => `address-section-title-${this.sectionKey()}`);

  protected readonly sectionErrorMessages = computed((): FormErrorSummaryItem[] => {
    this.translationService.revision();
    this.validationAttempt();

    const summaryExtras =
      this.addressSuggestion()?.mode === 'suggestion'
        ? [
            {
              emphasis: this.translationService.translate('createShipment.fields.streetAddress'),
              detail: this.translationService.translate(
                'createShipment.validation.streetAddressUnverifiedSummaryDetail',
              ),
            },
          ]
        : undefined;

    return (
      collectAddressSectionErrors(
        this.addressFormGroup,
        (key, params) => this.translationService.translate(key, params),
        this.submitted(),
        undefined,
        summaryExtras,
        this.sectionKey(),
      )?.summaryMessages ?? []
    );
  });

  /**
   * Subscribes to streetAddress and addressLine3 value changes to update the rural route signal
   * on every keystroke so the warning appears before save is clicked.
   */
  ngOnInit(): void {
    const group = this.addressFormGroup;
    const bump = (): void => this.ruralRouteVersion.update((v) => v + 1);

    group
      .get('streetAddress')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bump);

    group
      .get('addressLine3')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(bump);
  }

  protected readonly ruralRouteState = computed(() => {
    this.ruralRouteVersion();
    this.validationAttempt();
    const group = this.addressFormGroup;
    const streetAddress = String(group.get('streetAddress')?.value ?? '');
    const addressLine3 = String(group.get('addressLine3')?.value ?? '');
    const detected = detectPostalKeywordInAddress(streetAddress, addressLine3);

    if (!detected) {
      return { detected: false, civicAddressRequired: false, phoneRequired: false };
    }

    const civicAddressRequired = !String(group.get('addressLine2')?.value ?? '').trim();
    const phoneRequired = !String(group.get('phoneNumber')?.value ?? '').trim();

    return { detected: true, civicAddressRequired, phoneRequired };
  });

  protected readonly showRuralRouteInfo = computed(() => this.ruralRouteState().detected);

  protected readonly showRuralRouteErrorBanner = computed(() => {
    if (!this.submitted()) {
      return false;
    }

    const state = this.ruralRouteState();
    if (!state.detected) {
      return false;
    }

    // OM-692: for Ship To (recipient) the phone requirement is already represented
    // in the top error summary, so we avoid showing the duplicate phone bullet
    // inside this rural/PO Box banner.
    return state.civicAddressRequired || (state.phoneRequired && this.sectionKey() !== 'shipTo');
  });

  protected readonly ruralRoutePhoneBulletKey = computed(() =>
    this.sectionKey() === 'shipFrom'
      ? 'createShipment.validation.ruralRouteErrorBannerSenderPhone'
      : 'createShipment.validation.ruralRouteErrorBannerRecipientPhone',
  );

  /**
   * Whether the Email and Phone Number fields should be marked required in the UI.
   * For Ship To, these become required only when recipient notifications are enabled;
   * Ship From keeps them always required.
   */
  protected contactFieldsRequired(): boolean {
    if (this.sectionKey() !== 'shipTo') {
      return true;
    }

    return this.addressFormGroup.get('receiveNotifications')?.value === true;
  }

  /**
   * Builds a section-scoped input name so browsers do not autofill AVS fields heuristically.
   */
  protected avsFieldName(field: string): string {
    return `${this.sectionKey()}-${field}`;
  }

  /**
   * Returns the inline validation message for a field after blur, submit, or AVS verification.
   */
  protected fieldError(field: AddressValidatedFieldKey): string | null {
    this.translationService.revision();
    this.fieldInteractionVersion();

    if (field === 'streetAddress' && this.addressSuggestion()?.mode === 'suggestion') {
      return this.translationService.translate('createShipment.validation.streetAddressUnverified');
    }

    return getAddressFieldErrorMessage(
      this.addressFormGroup.get(field),
      field,
      (key, params) => this.translationService.translate(key, params),
      this.submitted(),
    );
  }

  /**
   * Marks a field touched on blur so applicable UI validators can show inline messages.
   */
  protected onFieldBlur(field: AddressValidatedFieldKey): void {
    blurAddressField(this.addressFormGroup.get(field));
    this.fieldInteractionVersion.update((version) => version + 1);
  }

  /**
   * Emits when the user chooses to clear this section only.
   */
  protected onClearForm(): void {
    this.clearRequested.emit();
  }

  /**
   * Expands optional address line fields for this section.
   */
  protected onExpandAddressLines(): void {
    this.expandLinesRequested.emit();
  }

  /**
   * Propagates the return-address checkbox value to the parent form.
   */
  protected onReturnAddressToggle(checked: boolean): void {
    this.useDifferentReturnAddressChange.emit(checked);
  }

  /**
   * Emits when the user accepts the AVS suggested address for this section.
   */
  protected onUseSuggestedAddress(): void {
    this.useSuggestedAddress.emit();
  }

  /**
   * Emits when the user keeps their original address entry.
   */
  protected onKeepSuggestedAddressEntry(): void {
    this.keepSuggestedAddressEntry.emit();
  }
}
